import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, MessageSquare, ShieldCheck, Heart } from "lucide-react";
import { useAuth, authFetch } from "@/lib/auth";

const links = [
  { label: "Marketplace", href: "/marketplace", external: true },
  { label: "List My Bike", href: "/list-my-bike", external: true },
  { label: "Sell Directly", href: "/sell-my-bike", external: true },
  { label: "RevvPass", href: "#revvpass" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { user, login, logout } = useAuth() || {};
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let dead = false;
    const load = async () => {
      try {
        const r = await authFetch("/api/conversations/unread-count");
        if (r.ok) { const j = await r.json(); if (!dead) setUnread(j.unread || 0); }
      } catch {}
    };
    load();
    const t = setInterval(load, 10000);
    return () => { dead = true; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3 backdrop-blur-xl bg-[#0D0F12]/85 border-b border-white/10" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Biker Boulevard"
            className="w-10 h-10 rounded-md object-contain"
            style={{ filter: "drop-shadow(0 0 12px rgba(0,204,204,0.4))" }}
          />
          <div className="flex flex-col leading-none">
            <span className="font-[Unbounded] text-sm font-bold tracking-tight text-white">BIKER</span>
            <span className="font-[Unbounded] text-sm font-bold tracking-tight text-[#00cccc]">BOULEVARD</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            l.external ? (
              <Link key={l.href} to={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-zinc-400 hover:text-[#00cccc] transition-colors duration-300 font-medium">{l.label}</Link>
            ) : (
              <a key={l.href} href={isHome ? l.href : `/${l.href}`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-zinc-400 hover:text-[#00cccc] transition-colors duration-300 font-medium">{l.label}</a>
            )
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="relative">
              <button onClick={() => setMenu((v) => !v)} data-testid="user-menu-trigger"
                className="relative w-10 h-10 rounded-full border border-white/20 overflow-hidden flex items-center justify-center hover:border-[#00cccc]/50">
                {user.picture ? <img src={user.picture} alt="Me" className="w-full h-full object-cover" /> : <span className="text-xs">{(user.name || "R")[0]}</span>}
              </button>
              {unread > 0 && <span data-testid="nav-unread-dot" className="absolute top-0 right-0 w-3 h-3 rounded-full bg-red-500 border-2 border-[#0D0F12] pointer-events-none" />}
              {menu && <AccountMenu user={user} unread={unread} onClose={() => setMenu(false)} logout={logout} />}
            </div>
          ) : (
            <button onClick={login} data-testid="navbar-login" className="text-sm text-zinc-400 hover:text-[#00cccc] transition-colors">Sign in</button>
          )}
        </div>

        {/* Mobile right cluster: account avatar (logged in) / sign-in, then nav hamburger */}
        <div className="flex md:hidden items-center gap-1.5">
          {user ? (
            <div className="relative">
              <button onClick={() => { setMenu((v) => !v); setOpen(false); }} data-testid="user-menu-trigger-mobile"
                className="relative w-9 h-9 rounded-full border border-white/20 overflow-hidden flex items-center justify-center hover:border-[#00cccc]/50"
                aria-label="Account menu">
                {user.picture ? <img src={user.picture} alt="Me" className="w-full h-full object-cover" /> : <span className="text-xs text-white">{(user.name || "R")[0]}</span>}
              </button>
              {unread > 0 && <span data-testid="nav-unread-dot-mobile" className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-[#0D0F12] pointer-events-none" />}
              {menu && <AccountMenu user={user} unread={unread} onClose={() => setMenu(false)} logout={logout} mobile />}
            </div>
          ) : (
            <button onClick={login} data-testid="navbar-login-mobile" className="px-3.5 py-1.5 text-xs font-semibold text-black bg-[#00cccc] rounded-full">Sign in</button>
          )}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => { setOpen(!open); setMenu(false); }}
            className="w-10 h-10 flex items-center justify-center text-white"
            aria-label="Toggle navigation menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden mt-4 mx-6 glass rounded-xl p-4"
        >
          <div className="px-2 pb-2 text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">// Navigate</div>
          <nav className="flex flex-col">
            {links.map((l) => (
              l.external ? (
                <Link key={l.href} to={l.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3 py-3 rounded-lg text-base text-zinc-200 hover:text-[#00cccc] hover:bg-white/[0.04] transition-colors">{l.label}</Link>
              ) : (
                <a key={l.href} href={isHome ? l.href : `/${l.href}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3 py-3 rounded-lg text-base text-zinc-200 hover:text-[#00cccc] hover:bg-white/[0.04] transition-colors">{l.label}</a>
              )
            ))}
          </nav>
          {!user && (
            <button onClick={login} data-testid="mobile-signin-cta" className="mt-3 w-full px-5 py-3 text-sm font-semibold text-black bg-[#00cccc] rounded-full">
              Sign in with Google
            </button>
          )}
        </motion.div>
      )}
    </motion.header>
  );
}

function AccountMenu({ user, unread, onClose, logout, mobile = false }) {
  const tid = (base) => (mobile ? `${base}-mobile` : base);
  return (
    <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#1A1E23] p-2 shadow-xl z-50" data-testid={tid("user-menu")}>
      <div className="px-3 py-2 border-b border-white/10 mb-1">
        <div className="text-sm font-semibold truncate text-white">{user.name || "Rider"}</div>
        <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>
      </div>
      <Link to="/dashboard" onClick={onClose} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.06] text-sm" data-testid={tid("menu-dashboard")}><LayoutDashboard className="w-4 h-4 text-[#00cccc]" /> My Listings</Link>
      <Link to="/favourites" onClick={onClose} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.06] text-sm" data-testid={tid("menu-favourites")}><Heart className="w-4 h-4 text-[#00cccc]" /> Favourites</Link>
      <Link to="/messages" onClick={onClose} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.06] text-sm" data-testid={tid("menu-messages")}><MessageSquare className="w-4 h-4 text-[#00cccc]" /> Messages{unread > 0 && <span data-testid={tid("menu-messages-unread")} className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[18px] text-center">{unread}</span>}</Link>
      {user.is_admin && (
        <Link to="/admin" onClick={onClose} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.06] text-sm" data-testid={tid("menu-admin")}><ShieldCheck className="w-4 h-4 text-[#00cccc]" /> Admin Panel</Link>
      )}
      <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.06] text-sm text-left" data-testid={tid("menu-logout")}><LogOut className="w-4 h-4 text-red-400" /> Logout</button>
    </div>
  );
}
